import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import bulletinService from '../services/bulletinService';

const BulletinDetail = () => {
  const [bulletin, setBulletin] = useState(null);
  const [loading, setLoading] = useState(true);
  const { id } = useParams();

  // Priority color mapping
  const priorityColors = {
    high: 'bg-red-500',
    medium: 'bg-yellow-500',
    low: 'bg-green-500'
  };

  // Dummy data for single bulletin (matching the dummy data from UserBulletinBoard)
  const dummyBulletin = {
    id: parseInt(id),
    title: `Weather Bulletin #${id}`,
    description: `This is a detailed weather bulletin with comprehensive information about current conditions and forecasts. Bulletin #${id} contains important updates that residents should be aware of.`,
    category: "Weather Alert",
    priority: id % 3 === 0 ? "high" : id % 2 === 0 ? "medium" : "low",
    imageUrl: `https://images.unsplash.com/photo-${1550000000000 + parseInt(id) * 1000000}?w=800&h=400&fit=crop`,
    content: `Detailed content for bulletin #${id}:

This bulletin provides comprehensive weather information and updates for the region. Please read carefully and take appropriate action based on the priority level indicated.

Key Points:
• Monitor local weather conditions
• Stay informed about changing patterns
• Take necessary precautions
• Share information with neighbors

For more information, contact your local weather station or visit our website for real-time updates.`
  };

  useEffect(() => {
    const fetchBulletin = async () => {
      try {
        // For now, use dummy data instead of API
        setBulletin(dummyBulletin);
        setLoading(false);
        
        // Uncomment below to use real API data
        /*
        const data = await bulletinService.getBulletinById(id);
        setBulletin(data);
        setLoading(false);
        */
      } catch (error) {
        console.error('Failed to fetch bulletin', error);
        // Fallback to dummy data on error
        setBulletin(dummyBulletin);
        setLoading(false);
      }
    };

    fetchBulletin();
  }, [id]);

  if (loading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (!bulletin) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
        <div className="text-center text-white">
          <h2 className="text-2xl font-bold mb-4">Bulletin Not Found</h2>
          <Link to="/Dashboard/BulletinBoard" className="text-blue-400 hover:text-blue-300">
            ← Back to Bulletin Board
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-4xl mx-auto">
      {/* Back Button */}
      <div className="mb-6">
        <Link 
          to="/Dashboard/BulletinBoard" 
          className="inline-flex items-center text-blue-400 hover:text-blue-300 transition-colors duration-200"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Bulletin Board
        </Link>
      </div>

      {/* Main Content */}
      <div className="group relative overflow-hidden rounded-xl shadow-2xl" style={{ backgroundColor: '#212C43' }}>
        {/* Hero Image Section */}
        {bulletin.imageUrl && (
          <div className="relative h-64 md:h-80 overflow-hidden">
            <img 
              className="w-full h-full object-cover" 
              src={bulletin.imageUrl} 
              alt={bulletin.title}
              onError={(e) => {
                e.target.src = 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=400&fit=crop';
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
            
            {/* Priority Badge */}
            <div className={`absolute top-4 right-4 px-4 py-2 rounded-full text-sm font-bold text-white ${priorityColors[bulletin.priority] || 'bg-gray-500'}`}>
              {bulletin.priority.toUpperCase()} PRIORITY
            </div>
            
            {/* Category Badge */}
            <div className="absolute top-4 left-4 px-4 py-2 rounded-full text-sm font-semibold text-white bg-blue-600/80 backdrop-blur-sm">
              {bulletin.category}
            </div>
            
            {/* Title Overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
                {bulletin.title}
              </h1>
            </div>
          </div>
        )}

        {/* Content Section */}
        <div className="p-8">
          {/* Meta Information */}
          <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-600">
            <div className="flex items-center space-x-4">
              <div className="flex items-center text-gray-300">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a2 2 0 012-2h4a2 2 0 012 2v4m-6 0h6m-6 0l-2 2m8-2l2 2m-2 0h2m-2 0V7m0 8v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4m12-2h2m-2 0V9m-2 2l2-2m-2 2l-2-2" />
                </svg>
                <span className="text-sm">Weather Bulletin #{bulletin.id}</span>
              </div>
              <div className="flex items-center text-gray-300">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm">Published: {new Date().toLocaleDateString()}</span>
              </div>
            </div>
            
            <div className={`px-3 py-1 rounded-full text-xs font-semibold text-white ${priorityColors[bulletin.priority] || 'bg-gray-500'}`}>
              {bulletin.priority.toUpperCase()}
            </div>
          </div>

          {/* Description */}
          <div className="mb-8">
            <h2 className="text-xl font-bold text-white mb-4">Summary</h2>
            <p className="text-gray-300 leading-relaxed text-lg">
              {bulletin.description}
            </p>
          </div>

          {/* Detailed Content */}
          {bulletin.content && (
            <div className="mb-8">
              <h2 className="text-xl font-bold text-white mb-4">Detailed Information</h2>
              <div className="text-gray-300 leading-relaxed whitespace-pre-line">
                {bulletin.content}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-600">
            <button className="flex items-center justify-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
              </svg>
              Share Bulletin
            </button>
            
            <button className="flex items-center justify-center px-6 py-3 border border-blue-500 text-blue-400 hover:bg-blue-500 hover:text-white rounded-lg transition-all duration-200">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download PDF
            </button>
          </div>
        </div>

        {/* Hover Effect Border */}
        <div className="absolute inset-0 border-2 border-transparent group-hover:border-blue-400 rounded-xl transition-all duration-300 opacity-0 group-hover:opacity-100"></div>
      </div>
    </div>
  );
};

export default BulletinDetail;